/**
 * "Your own books" (TMMAPP-0090) — the guest pitch page. YES opens the subscribe dialog in
 * `offer` mode (TMMAPP-0116).
 * NOT TODAY confirms with "No problem." and Done returns to where the guest came from.
 * Subscribers and the NONE tier never stay here.
 */
import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cardGridSx } from '@/components/cardGrid';
import { SubmitButton } from '@/components/SubmitButton';
import { useOpenSubscribe } from '@/features/subscribe/subscribeOfferContext';
import { useMe } from '@/session/meHooks';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { resolveTokens } from '@/theme/theme';
import { safeInternalPath, sponsorName } from './sponsorName';

export function OwnBooksPage() {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const [noProblem, setNoProblem] = useState(false);
  const openSubscribe = useOpenSubscribe();
  const title = usePageTitle({ title: t('guest.pitchTitle') });

  if (me.tier === 'SUBSCRIBER') return <Navigate to="/me/virtual-accounts" replace />;
  if (me.tier !== 'GUEST') return <Navigate to="/me/accounts" replace />;

  const shared = me.virtualAccounts.find((virtualAccount) => !virtualAccount.mine);
  const sponsor = sponsorName(me, shared?.id ?? null);
  // No sponsor name: the pitch says "the virtual account you were invited to" (TMMAPP-0097 B9).
  const named = sponsor.length > 0;
  const virtualAccount = shared?.name ?? t('guest.sharedFallback');
  const from = safeInternalPath(
    typeof location.state === 'object' && location.state !== null && 'from' in location.state
      ? (location.state as { from?: unknown }).from
      : null,
  );
  const backTo =
    from !== null && !from.startsWith('/welcome') && !from.startsWith('/me/own-books')
      ? from
      : shared
        ? `/v/${shared.id}`
        : '/me/virtual-accounts';

  const cards = [
    { title: t('guest.benefitAccountsTitle'), body: t('guest.benefitAccountsBody') },
    { title: t('guest.benefitBucketsTitle'), body: t('guest.benefitBucketsBody') },
    {
      title: t('guest.benefitShareTitle'),
      body: named ? t('guest.benefitShareBody', { sponsor }) : t('guest.benefitShareBodyNoSponsor'),
    },
  ];

  function closeAndReturn() {
    setNoProblem(false);
    void navigate(backTo);
  }

  return (
    <Box sx={{ maxWidth: 760 }}>
      <PageHeader
        title={title}
        subtitle={named ? t('guest.pitchIntro', { sponsor }) : t('guest.pitchIntroNoSponsor')}
      />
      <Box sx={cardGridSx()}>
        {cards.map((card) => (
          <Paper key={card.title} elevation={1} sx={{ p: 2.25, height: '100%' }}>
            <Typography variant="h3" component="h2">
              {card.title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
              {card.body}
            </Typography>
          </Paper>
        ))}
      </Box>
      <Box
        sx={{
          mt: 2.25,
          px: 2.25,
          py: 2,
          borderRadius: 1,
          bgcolor: (theme) => resolveTokens(theme).brandSoft,
          color: (theme) => resolveTokens(theme).text,
        }}
      >
        <Typography variant="body2" component="p">
          {t('guest.pitchNote', { virtualAccount })}
        </Typography>
      </Box>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5, mt: 2.75 }}>
        <Button variant="outlined" onClick={() => setNoProblem(true)}>
          {t('guest.notToday')}
        </Button>
        <Button variant="contained" onClick={openSubscribe}>
          {t('guest.yesSubscribe')}
        </Button>
      </Stack>

      <Dialog open={noProblem} onClose={closeAndReturn} fullWidth maxWidth="xs">
        <DialogTitle>{t('guest.noProblemTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {named
              ? t('guest.noProblem', { virtualAccount, sponsor })
              : t('guest.noProblemNoSponsor', { virtualAccount })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SubmitButton type="button" onClick={closeAndReturn}>
            {t('guest.done')}
          </SubmitButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
